'use client';

import * as React from 'react';

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface BreakpointConfig {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  '2xl': number;
}

const defaultBreakpoints: BreakpointConfig = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

export interface BreakpointState {
  /**
   * Current breakpoint
   */
  breakpoint: Breakpoint;
  /**
   * Whether the current screen size is at or above the specified breakpoint
   */
  isAbove: (breakpoint: Breakpoint) => boolean;
  /**
   * Whether the current screen size is below the specified breakpoint
   */
  isBelow: (breakpoint: Breakpoint) => boolean;
  /**
   * Whether the current screen size is exactly at the specified breakpoint
   */
  isAt: (breakpoint: Breakpoint) => boolean;
  /**
   * Whether the current screen size is between two breakpoints (inclusive)
   */
  isBetween: (min: Breakpoint, max: Breakpoint) => boolean;
  /**
   * Current window width
   */
  width: number;
}

/**
 * Hook that tracks the current breakpoint based on window width
 * Provides utility functions to check breakpoint relationships
 *
 * @param breakpoints - Optional custom breakpoint configuration
 * @returns BreakpointState with current breakpoint and utility functions
 */
export function useBreakpoint(breakpoints: BreakpointConfig = defaultBreakpoints): BreakpointState {
  // Always initialize with SSR-safe defaults to prevent hydration mismatch.
  // Real values are set in useEffect after mount.
  const [state, setState] = React.useState<BreakpointState>({
    breakpoint: 'xs',
    isAbove: () => false,
    isBelow: () => false,
    isAt: (bp: Breakpoint) => bp === 'xs',
    isBetween: () => false,
    width: 0,
  });

  React.useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      const breakpoint = getBreakpoint(width, breakpoints);

      setState({
        breakpoint,
        isAbove: (bp: Breakpoint) => isAboveBreakpoint(width, bp, breakpoints),
        isBelow: (bp: Breakpoint) => isBelowBreakpoint(width, bp, breakpoints),
        isAt: (bp: Breakpoint) => breakpoint === bp,
        isBetween: (min: Breakpoint, max: Breakpoint) =>
          isBetweenBreakpoints(width, min, max, breakpoints),
        width,
      });
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [breakpoints]);

  return state;
}

/**
 * Get the current breakpoint based on width
 */
function getBreakpoint(width: number, breakpoints: BreakpointConfig): Breakpoint {
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
}

/**
 * Check if width is above a breakpoint
 */
function isAboveBreakpoint(
  width: number,
  breakpoint: Breakpoint,
  breakpoints: BreakpointConfig,
): boolean {
  return width >= breakpoints[breakpoint];
}

/**
 * Check if width is below a breakpoint
 */
function isBelowBreakpoint(
  width: number,
  breakpoint: Breakpoint,
  breakpoints: BreakpointConfig,
): boolean {
  return width < breakpoints[breakpoint];
}

/**
 * Check if width is between two breakpoints (inclusive)
 */
function isBetweenBreakpoints(
  width: number,
  min: Breakpoint,
  max: Breakpoint,
  breakpoints: BreakpointConfig,
): boolean {
  const minWidth = breakpoints[min];
  const maxWidth = breakpoints[max];
  return width >= minWidth && width < maxWidth;
}

/**
 * Convenience hook for mobile detection
 * @param breakpoint - Breakpoint to consider as mobile (default: "md")
 * @returns boolean indicating if current screen is mobile
 */
export function useIsMobile(breakpoint: Breakpoint = 'md'): boolean {
  const { isBelow } = useBreakpoint();
  return isBelow(breakpoint);
}

/**
 * Convenience hook for tablet detection
 * @returns boolean indicating if current screen is tablet-sized
 */
export function useIsTablet(): boolean {
  const { isBetween } = useBreakpoint();
  return isBetween('md', 'lg');
}

/**
 * Convenience hook for desktop detection
 * @param breakpoint - Breakpoint to consider as desktop (default: "lg")
 * @returns boolean indicating if current screen is desktop
 */
export function useIsDesktop(breakpoint: Breakpoint = 'lg'): boolean {
  const { isAbove } = useBreakpoint();
  return isAbove(breakpoint);
}

/**
 * Hook that returns a value based on the current breakpoint
 * Useful for dynamic props that can't be handled with CSS classes
 *
 * @param values - Object mapping breakpoints to values
 * @param defaultValue - Default value for breakpoints not specified
 * @returns The value for the current breakpoint
 */
export function useBreakpointValue<T>(values: Partial<Record<Breakpoint, T>>, defaultValue: T): T {
  const { breakpoint } = useBreakpoint();

  // Find the highest breakpoint that matches or is below current
  const breakpoints: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
  const currentIndex = breakpoints.indexOf(breakpoint);

  // Look for the closest breakpoint value (current or lower)
  for (let i = currentIndex; i >= 0; i--) {
    const bp = breakpoints[i];
    if (values[bp] !== undefined) {
      return values[bp]!;
    }
  }

  return defaultValue;
}
